/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the phone/PWA workspace shell independently from desktop chrome.
 * @state-owner navigation store owns workspace screens; local state owns phone utility pages
 * @allowed-imports React; app providers; workspace store; shared components; sibling mobile pages
 * @forbidden desktop rail, desktop top bar, panel resizing, direct API client
 * @public-api WorkspaceShell
 * @side-effects workspace navigation, search opening, anchored-menu presentation
 * @tests frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { SearchIcon } from '../../shared/components/VispoIcon';
import type { WorkspaceSection } from '../../shared/lib/model';
import { mobileNavigationSections } from '../../shared/lib/workspace-navigation';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { MobileListColumn, useWorkspaceListContent } from './ListColumn';
import { MobileMainColumn } from './MainColumn';
import { MobileAccountPlaceholderPage } from './MobileAccountPages';
import { MobileInboxPage } from './MobileInboxPage';
import { MobileTopBar, type MobileUtilityPage } from './MobileTopBar';

export function MobileWorkspaceShell({
  onNavigate,
  onOpenSearch,
}: {
  onNavigate(section: WorkspaceSection): void;
  onOpenSearch(): void;
}) {
  const role = useActiveRole();
  const navigate = useNavigate();
  const t = useTranslator();
  const section = useWorkspaceUiStore((state) => state.section);
  const mobileMainOpen = useWorkspaceUiStore((state) => state.mobileMainOpen);
  const content = useWorkspaceListContent();
  const [utilityPage, setUtilityPage] = useState<MobileUtilityPage | null>(null);
  const [showBadgeCount, setShowBadgeCount] = useState(true);
  const mobileNavigation = mobileNavigationSections(role);
  const focusedWorkspace = mobileMainOpen && (section === 'act' || section === 'live');
  const hideBottomNavigation = focusedWorkspace;

  useEffect(() => {
    const closePage = () => setUtilityPage(null);
    window.addEventListener('popstate', closePage);
    return () => window.removeEventListener('popstate', closePage);
  }, []);

  const openUtilityPage = (page: MobileUtilityPage) => {
    window.history.pushState({ vispoMobilePage: page }, '');
    setUtilityPage(page);
  };

  const closeUtilityPage = () => {
    if (window.history.state?.vispoMobilePage) window.history.back();
    else setUtilityPage(null);
  };

  const navigateSection = (next: WorkspaceSection) => {
    if (window.history.state?.vispoMobilePage) {
      window.history.replaceState({}, '');
    }
    setUtilityPage(null);
    onNavigate(next);
  };

  const workspace = mobileMainOpen ? (
    <MobileMainColumn />
  ) : (
    <>
      <MobileTopBar
        onOpenPage={openUtilityPage}
        onOpenSettings={() => navigate('/teacher/settings/account')}
        showBadgeCount={showBadgeCount}
        title={content.title}
      />
      <MobileListColumn content={content} />
    </>
  );

  return (
    <div className="mobile-workspace-shell">
      {utilityPage === 'inbox' ? (
        <MobileInboxPage
          onBack={closeUtilityPage}
          onShowBadgeCountChange={setShowBadgeCount}
          showBadgeCount={showBadgeCount}
        />
      ) : utilityPage === 'profile' || utilityPage === 'notifications' ? (
        <MobileAccountPlaceholderPage kind={utilityPage} onBack={closeUtilityPage} />
      ) : (
        workspace
      )}

      {!hideBottomNavigation ? (
        <nav aria-label={t('主要導覽')} className="mobile-bottom-navigation">
          <div className="mobile-bottom-navigation__primary">
            {mobileNavigation.map((item) => (
              <button
                aria-label={t(item.label)}
                aria-current={section === item.k && !utilityPage ? 'page' : undefined}
                className={section === item.k && !utilityPage ? 'is-active' : ''}
                key={item.k}
                type="button"
                onClick={() => navigateSection(item.k as WorkspaceSection)}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d={item.path} />
                </svg>
              </button>
            ))}
          </div>
          <button
            aria-label={t('搜尋')}
            className="mobile-bottom-navigation__search"
            type="button"
            onClick={onOpenSearch}
          >
            <SearchIcon />
          </button>
        </nav>
      ) : null}
    </div>
  );
}
